import { useEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";

// Časové pole bez nativního pickeru prohlížeče. Nativní `<input type="time">`
// otevírá kolotoč, který se donekonečna protáčí (00 -> 59 -> 00) a nedá se
// ovlivnit CSS ani JS. Tady jsou to dva obyčejné scrollovací sloupce — mají
// začátek i konec, takže scroll nahoru skončí na 00 a dolů na 23 / 55.
// Zároveň jde čas rovnou napsat („730" -> 07:30), což je na klávesnici nejrychlejší.

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MIN_STEP = 5;
const BASE_MINUTES = Array.from({ length: 60 / MIN_STEP }, (_, i) => String(i * MIN_STEP).padStart(2, "0"));

const pad = (n) => String(n).padStart(2, "0");

// "730" -> "07:30", "7:5" -> "07:05", "1530" -> "15:30". Vrací "" když nejde přečíst.
export function normalizeTime(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const digits = s.replace(/\D/g, "");
  let h, m;
  if (s.includes(":")) {
    const [a, b] = s.split(":");
    h = parseInt(a, 10); m = parseInt(b || "0", 10);
  } else if (digits.length <= 2) {
    h = parseInt(digits, 10); m = 0;
  } else if (digits.length === 3) {
    h = parseInt(digits.slice(0, 1), 10); m = parseInt(digits.slice(1), 10);
  } else {
    h = parseInt(digits.slice(0, 2), 10); m = parseInt(digits.slice(2, 4), 10);
  }
  if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return "";
  return `${pad(h)}:${pad(m)}`;
}

export function TimeField({ value, onChange, testid, label, className = "" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || "");
  const wrapRef = useRef(null);
  const hourRef = useRef(null);
  const minRef = useRef(null);

  useEffect(() => { setText(value || ""); }, [value]);

  // Klik mimo / Escape zavře panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const [curH, curM] = (normalizeTime(value) || ":").split(":");

  // Když má uživatel minutu mimo krok (např. 07:03), přidej ji do seznamu, ať je vidět jako vybraná.
  const minutes = useMemo(() => {
    if (curM && !BASE_MINUTES.includes(curM)) {
      return [...BASE_MINUTES, curM].sort((a, b) => Number(a) - Number(b));
    }
    return BASE_MINUTES;
  }, [curM]);

  // Po otevření doscrolluj sloupce na aktuální hodnotu (bez protáčení — jen posun).
  useEffect(() => {
    if (!open) return;
    for (const ref of [hourRef, minRef]) {
      const el = ref.current?.querySelector('[data-selected="true"]');
      if (el) el.scrollIntoView({ block: "center" });
    }
  }, [open]);

  const commit = (t) => { onChange(t); setText(t); };
  const pick = (h, m) => {
    const t = `${h}:${m}`;
    commit(t);
    // Panel necháme otevřený, ať jde doladit i druhý sloupec.
  };

  const onBlurInput = () => {
    const t = normalizeTime(text);
    if (t) commit(t);
    else setText(value || "");   // nečitelný vstup -> vrať původní hodnotu
  };

  const Column = ({ items, selected, onPick, colRef, tid }) => (
    <div ref={colRef} data-testid={tid}
      className="max-h-48 overflow-y-auto overscroll-contain flex-1 py-1 [scrollbar-width:thin]">
      {items.map((v) => {
        const isSel = v === selected;
        return (
          <button key={v} type="button" data-selected={isSel} onClick={() => onPick(v)}
            className={`w-full py-2 text-center text-sm tabular-nums rounded-md transition-colors ${isSel
              ? "bg-brand text-white font-bold"
              : "hover:bg-secondary text-foreground"}`}>
            {v}
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text" inputMode="numeric" placeholder="--:--"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={onBlurInput}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onBlurInput(); setOpen(false); } }}
          data-testid={testid}
          aria-label={label}
          className="h-11 w-full min-w-0 rounded-[14px] border border-border bg-background px-3 pr-10 text-sm tabular-nums outline-none focus:border-brand transition-colors"
        />
        <button type="button" onClick={() => setOpen((o) => !o)} tabIndex={-1}
          data-testid={testid ? `${testid}-toggle` : undefined}
          aria-label="Vybrat čas"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-brand transition-colors">
          <Clock size={16} />
        </button>
      </div>

      {open && (
        <div data-testid={testid ? `${testid}-panel` : undefined}
          className="absolute z-50 mt-1 w-full rounded-[14px] border border-border bg-card shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
            <span className="flex-1 text-center">Hod</span>
            <span className="flex-1 text-center">Min</span>
          </div>
          <div className="flex divide-x divide-border">
            <Column items={HOURS} selected={curH} colRef={hourRef} tid={testid ? `${testid}-hours` : undefined}
              onPick={(h) => pick(h, curM || "00")} />
            <Column items={minutes} selected={curM} colRef={minRef} tid={testid ? `${testid}-minutes` : undefined}
              onPick={(m) => pick(curH || "00", m)} />
          </div>
          <button type="button" onClick={() => setOpen(false)}
            className="w-full py-2 text-xs font-bold text-brand border-t border-border hover:bg-secondary transition-colors">
            Hotovo
          </button>
        </div>
      )}
    </div>
  );
}
